import { useForm } from 'react-hook-form';
import { useEffectOnce } from 'react-use';
import { zodResolver } from '@hookform/resolvers/zod';
import cns from 'classnames';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import * as z from 'zod';
import { useValidateWithdraw, ZepApiError, ZepApiStatusType } from '@zep/apis';
import { useAuth } from '@zep/auth';
import { logger } from '@zep/core-logger';
import { useDialog } from '@zep/dialog';
import { useZepBanWords } from '@zep/shared';
import { zusToast } from '@zep/toast';
import { User } from '@zep/types';
import { Button, Input, Spacing, TextField } from '@zep/ui';
import { useUserProfileMutation } from '../../services';
import { ProfileForm } from '../../types';
import S from './Profile.module.scss';
const IS_WITHDRAWAL_VISIBLE = false;
export const ProfilePageView = () => {
  const {
    user,
    login
  } = useAuth();
  const {
    t
  } = useTranslation(['accounts', 'common']);
  const dialog = useDialog();
  const {
    hasBanWords
  } = useZepBanWords();
  const {
    register,
    handleSubmit,
    getValues,
    reset,
    setError,
    formState: {
      errors,
      isValid,
      isDirty
    }
  } = useForm({
    resolver: zodResolver(z.object<{ [key in keyof ProfileForm]: z.ZodTypeAny }>({
      name: z.string().min(1, t('accounts:account_profile_name_required')).max(14, t('accounts:account_profile_input_max_length', {
        maxLength: 14
      })),
      marketName: z.string().max(14, t('accounts:account_profile_input_max_length', {
        maxLength: 14
      }))
    }), undefined, {
      mode: 'sync'
    }),
    mode: 'onChange',
    defaultValues: {
      name: user?.name || '',
      marketName: user?.finalMarketName || ''
    }
  });
  const userProfileMutation = useUserProfileMutation();
  const saveProfile = (formValues: ProfileForm) => {
    for (const [key, value] of Object.entries(formValues)) {
      if (key !== 'walletAddress' && value && hasBanWords(value)) {
        const title = t('common:alert.badWords', {
          target: key === 'name' ? t('accounts:account_profile_name') : t('accounts:account_profile_final_market_name')
        });
        void dialog.alert({
          title
        });
        return setError(key as 'name' | 'marketName', {
          message: title
        });
      }
    }
    userProfileMutation.mutateAsync(formValues).then(data => {
      logger.track('set_player_name', {
        previous_nickname: user?.name,
        new_nickname: formValues.name
      });
      const responseUserData = data.player;
      responseUserData.finalMarketName = responseUserData.marketName;
      responseUserData.walletAddress = responseUserData.walletAddress || '';
      reset(responseUserData, {
        keepDirty: false
      });
      zusToast.success(t('accounts:account_profile_save_complete'));
      void login(Object.assign({}, user, responseUserData) as User);
    }).catch(error => {
      void dialog.alert({
        title: t(error.message)
      });
    });
  };
  useEffectOnce(() => {
    if (getValues('name').length > 14) setError('name', {
      message: t('accounts:account_profile_input_max_length', {
        maxLength: 14
      })
    });
  });
  return <main className={S.main_wrapper} data-sentry-component="ProfilePageView" data-sentry-source-file="profile.tsx">
      <div className={S.title_wrapper}>
        <h3 className={S.contents_title}>
          {t('accounts:account_profile_setting')}
        </h3>
      </div>
      <div className={S.contents_wrapper}>
        <div className={S.text_field}>
          <TextField label={t('accounts:account_profile_name')} error={!!errors.name} helperText={errors.name?.message} data-sentry-element="TextField" data-sentry-source-file="profile.tsx">
            <Input {...register('name')} isFullWidth={true} placeholder={t('accounts:account_profile_name_placeholder')} maxLength={14} error={!!errors.name} data-sentry-element="Input" data-sentry-source-file="profile.tsx" />
          </TextField>
        </div>
        <div className={cns(S.text_field, S.separate_line)}>
          <TextField label={t('accounts:account_profile_final_market_name')} error={!!errors.marketName} helperText={errors.marketName && t('accounts:account_profile_input_max_length', {
          maxLength: 14,
          target: t('accounts:account_profile_final_market_name')
        })} data-sentry-element="TextField" data-sentry-source-file="profile.tsx">
            <div className={S.text_description}>
              {t('accounts:account_profile_final_market_name_description')}
            </div>
            <Input {...register('marketName')} isFullWidth={true} placeholder={t('accounts:account_profile_final_market_name_placeholder')} maxLength={14} error={!!errors.marketName} data-sentry-element="Input" data-sentry-source-file="profile.tsx" />
          </TextField>
        </div>
        {/* <div className={cns(S.text_field, S.separate_line)}>
          <ProfileWalletField />
         </div> */}
        <div>
          <Button onClick={handleSubmit(saveProfile)} isFullWidth={false} disabled={!isValid || !isDirty} className="min-w-[120px]" data-sentry-element="Button" data-sentry-source-file="profile.tsx">
            {t('accounts:account_profile_save')}
          </Button>
        </div>
        {IS_WITHDRAWAL_VISIBLE && <>
            <Spacing size={60} />
            <Withdrawal />
          </>}
      </div>
    </main>;
};
const Withdrawal = () => {
  const router = useRouter();
  const dialog = useDialog();
  const {
    t
  } = useTranslation(['common', 'accounts']);
  const {
    mutateAsync
  } = useValidateWithdraw();
  const handleWithdrawClick = async () => {
    logger.track('home_my_withdrawal');
    try {
      const res = await mutateAsync();
      if (res.status === 0) {
        await router.push('/accounts/withdraw');
      }
    } catch (error) {
      if (error instanceof ZepApiError) {
        if (error.status === ZepApiStatusType.NOT_ALLOWED_TO_DELETE_ACTIVE_PLAN) {
          await dialog.confirm({
            title: t('accounts:profile.accounts.validateWithdrawTitle'),
            content: t('accounts:profile.accounts.validateWithdrawContent'),
            confirmText: t('accounts:profile.accounts.validateWithdrawConfirmText'),
            confirmAction: async () => {
              await router.push('/me/spaces/plans');
            }
          });
          return;
        }
      }
      await dialog.alert({
        title: 'UNKNOWN ERROR',
        content: t('common:error.api.unknown')
      });
    }
  };
  return <div className={cns(S.text_field, S.separate_line)} data-sentry-component="Withdrawal" data-sentry-source-file="profile.tsx">
      <Button transparent size="xs" variant="sub" onClick={handleWithdrawClick} data-sentry-element="Button" data-sentry-source-file="profile.tsx">
        {t('accounts:profile.accounts.withdrawMembership')}
      </Button>
    </div>;
};